import {
  Popover as AntdPopover,
  type PopoverProps as AntdPopoverProps
} from 'antd'
import React from 'react'

import styled from 'styled-components'

export type PopoverProps = AntdPopoverProps

/**
 * AntD attaches the overlay element as a sibling of the trigger, so to style
 * the overlay we forward the styled-components className through to
 * `overlayClassName`. Using `forwardRef` here lets styled-components and AntD
 * attach refs without falling back to the deprecated `findDOMNode`, which
 * otherwise warned on every render of every Popover consumer.
 */
const MyPopover = React.forwardRef<HTMLElement, AntdPopoverProps>(
  ({ className, ...props }, ref) => (
    <AntdPopover
      {...props}
      overlayClassName={className}
      ref={ref as React.Ref<never>}
    />
  )
)
MyPopover.displayName = 'MyPopover'

const HORIZONTAL_PADDING = 16

const AntdPopoverStyled = styled(MyPopover)`
  .ant-popover-title {
    font-weight: 500;
    font-size: 18px;
    line-height: 24px;
  }

  .ant-popover-inner {
    border-radius: 2px;
    padding: 10px ${HORIZONTAL_PADDING}px;
  }

  .ant-popover-inner-content {
    font-weight: 400;
    font-size: 16px;
    line-height: 24px;

    // this helps us when we use multiple children
    // so every children can have this text props without writing them every time
    > * {
      font-weight: 400;
      font-size: 16px;
      line-height: 24px;
    }
  }

  .ant-popover-arrow {
    display: none !important;
  }
`

/**
 * N.B. Check out the AntD docs for more. https://ant.design/components/popover/
 */

export default function Popover({
  placement = 'bottomLeft',
  className,
  children,
  ...restProps
}: PopoverProps) {
  return (
    <AntdPopoverStyled
      placement={placement}
      {...restProps}
      overlayClassName={className}
    >
      {children}
    </AntdPopoverStyled>
  )
}
